import { combineReducers } from 'redux';

import { createNamedWrapperReducer } from '../../utils/createNamedWrapperReducer';
import { NotesAction, NotesActionKeys } from './actions';
import Utils from '../../utils';
import { Api } from '../../typings/api';

export enum NotesStatus {
	LOCAL = 'LOCAL',
	SHARED = 'SHARED',
}

export enum LoadingStatus {
	NOT_LOADED = 'NOT_LOADED',
	LOADING = 'LOADING',
	SAVING = 'SAVING',
	LOADED = 'LOADED',
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
// not to store 'content' and 'drawing' values in redux as they may have a huge size
export type NoteDataInRedux = Omit<Api.NoteData, 'content' | 'drawing'>;

export interface NoteState extends NoteDataInRedux {
	hasImage: boolean;
}

export interface ByIdState {
	[key: string]: NoteState;
}

interface EditorState {
	isOpened: boolean;
	noteId?: number;
}

export interface NotesState {
	[NotesStatus.LOCAL]: ByIdState;
	[NotesStatus.SHARED]: ByIdState;
	currentStatus: NotesStatus;
	editor: EditorState;
	loadingStatus: LoadingStatus;
}

const defaultCurrentStatusState: NotesStatus = NotesStatus.LOCAL;

const defaultEditorState: EditorState = {
	isOpened: false,
};

const defaultLoadingStatusState: LoadingStatus = LoadingStatus.NOT_LOADED;

const byId = (state: ByIdState = {}, action: NotesAction): ByIdState => {
	switch (action.type) {
		case NotesActionKeys.SET:
			return {
				...state,
				...Utils.itemsToById(action.items),
			};
		case NotesActionKeys.RESET:
			return {};
		case NotesActionKeys.CREATE:
			return {
				...state,
				[action.id]: action.data,
			};
		case NotesActionKeys.UPDATE:
			return {
				...state,
				[action.id]: {
					...state[action.id],
					...action.data,
				},
			};
		case NotesActionKeys.REMOVE: {
			const { [action.id]: id, ...rest } = state;
			return { ...rest };
		}
		default:
			return state;
	}
};

const currentStatus = (state: NotesStatus = defaultCurrentStatusState, action: NotesAction): NotesStatus => {
	switch (action.type) {
		case NotesActionKeys.SET_STATUS:
			return action.status;
		case NotesActionKeys.RESET:
			return defaultCurrentStatusState;
		default:
			return state;
	}
};

const editor = (state: EditorState = defaultEditorState, action: NotesAction): EditorState => {
	switch (action.type) {
		case NotesActionKeys.TOGGLE_EDITOR:
			return {
				...state,
				isOpened: !state.isOpened,
				noteId: action.id,
			};
		case NotesActionKeys.RESET:
			return defaultEditorState;
		default:
			return state;
	}
};

const loadingStatus = (state: LoadingStatus = defaultLoadingStatusState, action: NotesAction): LoadingStatus => {
	switch (action.type) {
		case NotesActionKeys.SET_LOADING_STATUS:
			return action.status;
		case NotesActionKeys.RESET:
			return defaultLoadingStatusState;
		default:
			return state;
	}
};

export const notes = combineReducers<NotesState>({
	[NotesStatus.LOCAL]: createNamedWrapperReducer(byId, NotesStatus.LOCAL),
	[NotesStatus.SHARED]: createNamedWrapperReducer(byId, NotesStatus.SHARED),
	currentStatus,
	editor,
	loadingStatus,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/NotesPage/reducer.ts